import { useCallback, useMemo, useState } from 'react'
import { Text, View } from 'react-native'
import { useTranslation } from 'react-i18next'
import { Search, UserRoundSearch, X } from 'lucide-react-native'
import { type ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Avatar, StampProgress, TextField } from '@chillout/ui'
import { AdminScreen } from '../AdminScreen'
import { AdminTable } from '../shared/Table'
import { Card } from '../../shared/Card'
import { SectionPlaceholder } from '../shared/SectionPlaceholder'
import { useDebouncedValue } from '../../../hooks/useDebouncedValue'
import { useAdminMemberSearch, ADMIN_MEMBER_PAGE_SIZE } from './useAdminMemberSearch'
import type { AdminMemberSearchResult } from './useAdminMemberSearch'
import { MemberDetailView } from './MemberDetailView'

function getFullName(firstName: string | null, lastName: string | null): string {
  return [firstName, lastName].filter(Boolean).join(' ') || '—'
}

export function MembersScreen() {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null)

  const debouncedQuery = useDebouncedValue(searchQuery, 300)

  const searchResult = useAdminMemberSearch({ query: debouncedQuery, page })

  const members = searchResult.data?.results ?? []
  const totalCount = searchResult.data?.totalCount ?? 0
  const pageCount = Math.ceil(totalCount / ADMIN_MEMBER_PAGE_SIZE)
  const showResults = debouncedQuery.trim().length >= 2
  const isFetching = searchResult.isFetching && !searchResult.isLoading

  const handleClear = useCallback(() => {
    setSearchQuery('')
    setPage(1)
  }, [])

  const handleChangeText = useCallback((text: string) => {
    setSearchQuery(text)
    setPage(1)
  }, [])

  const columns = useMemo<ColumnDef<AdminMemberSearchResult>[]>(
    () => [
      {
        id: 'member',
        header: t('members.columns.member'),
        size: 150,
        cell: ({ row }) => (
          <View className="flex-row items-center gap-3">
            <Avatar name={getFullName(row.original.firstName, row.original.lastName)} email={row.original.email} size={40} />
            <Text className="min-w-0 flex-1 text-sm font-semibold text-primary" numberOfLines={1}>
              {getFullName(row.original.firstName, row.original.lastName)}
            </Text>
          </View>
        ),
      },
      {
        id: 'email',
        header: t('members.columns.email'),
        size: 200,
        cell: ({ row }) => (
          <Text className="text-sm font-normal text-primary" numberOfLines={1}>
            {row.original.email}
          </Text>
        ),
      },
      {
        id: 'stamps',
        header: t('members.columns.stamps'),
        size: 120,
        cell: ({ row }) => (
          <StampProgress current={row.original.currentStamps} total={row.original.stampTarget} />
        ),
      },
      {
        id: 'rewards',
        header: t('members.columns.rewards'),
        size: 80,
        cell: ({ row }) =>
          row.original.activeRewardCount > 0 ? (
            <Text className="text-sm font-normal text-primary">
              {t('members.rewardCount', { count: row.original.activeRewardCount })}
            </Text>
          ) : null,
      },
    ],
    [t],
  )

  const table = useReactTable({
    data: members,
    columns,
    getCoreRowModel: getCoreRowModel(),
  })

  if (selectedMemberId) {
    return (
      <MemberDetailView
        memberId={selectedMemberId}
        onBack={() => setSelectedMemberId(null)}
      />
    )
  }

  return (
    <AdminScreen
      title={t('adminDashboard.membersTitle')}
      subtitle={t('adminDashboard.membersSubtitle')}
    >
      <Card variant="dashboard" className="p-6">
        <TextField
          label={t('members.search.label')}
          placeholder={t('members.search.placeholder')}
          accessibilityLabel={t('members.search.accessibilityLabel')}
          prefix={Search}
          suffix={searchQuery ? X : undefined}
          onSuffixPress={searchQuery ? handleClear : undefined}
          suffixAccessibilityLabel={t('members.search.clearAccessibilityLabel')}
          value={searchQuery}
          onChangeText={handleChangeText}
          autoCapitalize="none"
          autoCorrect={false}
        />

        {!showResults ? (
          <SectionPlaceholder
            Icon={UserRoundSearch}
            title={t('members.emptyState.title')}
            description={t('members.emptyState.description')}
          />
        ) : (
          <>
            <View className="mt-6">
              <Text className="text-xl font-bold text-primary">
                {t('members.results.heading')}
              </Text>
              <Text className="mt-1 text-sm font-normal text-secondary">
                {t('members.results.count', { count: totalCount })}
              </Text>
            </View>

            <AdminTable
              table={table}
              minWidth={530}
              isLoading={searchResult.isLoading}
              isError={searchResult.isError}
              isFetching={isFetching}
              hasActiveFilters
              onRowPress={(member) => setSelectedMemberId(member.id)}
              rowAccessibilityLabel={(member) =>
                t('members.rowAccessibilityLabel', {
                  name: getFullName(member.firstName, member.lastName),
                  current: member.currentStamps,
                  target: member.stampTarget,
                  rewardCount: member.activeRewardCount,
                })
              }
              renderMobileRow={(member) => (
                <>
                  <View className="flex-row items-center gap-3">
                    <Avatar name={getFullName(member.firstName, member.lastName)} email={member.email} size={40} />
                    <View className="min-w-0 flex-1">
                      <Text className="text-sm font-semibold text-primary" numberOfLines={1}>
                        {getFullName(member.firstName, member.lastName)}
                      </Text>
                      <Text className="text-xs font-normal text-secondary" numberOfLines={1}>
                        {member.email}
                      </Text>
                    </View>
                  </View>
                  <View className="mt-2 flex-row items-center justify-between gap-3">
                    <View className="min-w-0 flex-1">
                      <StampProgress current={member.currentStamps} total={member.stampTarget} />
                    </View>
                    {member.activeRewardCount > 0 ? (
                      <Text className="shrink-0 text-xs font-normal text-secondary">
                        {t('members.rewardCount', { count: member.activeRewardCount })}
                      </Text>
                    ) : null}
                  </View>
                </>
              )}
              onRetry={() => void searchResult.refetch()}
              errorMessage={t('members.searchError')}
              noResultsDescription={t('members.results.noResults')}
              emptyIcon={UserRoundSearch}
              emptyTitle={t('members.emptyState.title')}
              emptyDescription={t('members.emptyState.description')}
              page={page}
              pageCount={pageCount}
              totalCount={totalCount}
              pageSize={ADMIN_MEMBER_PAGE_SIZE}
              onPreviousPage={() => setPage((p) => Math.max(p - 1, 1))}
              onNextPage={() => setPage((p) => Math.min(p + 1, pageCount))}
            />
          </>
        )}
      </Card>
    </AdminScreen>
  )
}